<template>
  <hb-basic-page
    :action-buttons="actionButtons"
    :back-button="{ show: true, fallback: 'survey_templates' }"
    :loading="loading"
    :subtle-loading="subtleLoading"
    :title="surveyTemplate.key ? $t('survey_templates.edit') : $t('survey_templates.create')"
    name="create-survey-template-view"
  >

    <v-col v-if="!loading" class="mb-10" cols="12" lg="12" md="12" xl="12">
      <v-tabs v-model="tab" color="primary">
        <v-tab>
          {{ $t('survey_templates.basic_info') }}
        </v-tab>
        <v-tab>
          {{ $t('survey_templates.questions') }}
        </v-tab>
      </v-tabs>
      <v-tabs-items v-model="tab" class="mt-5">
        <v-tab-item>
          <v-row>
            <v-col lg="6" md="8" offset-lg="3" offset-md="2" offset-xl="3" xl="6">
              <v-row>
                <v-col :class="$vuetify.breakpoint.xsOnly ? 'caption' : ''" cols="12">
                  {{ $t('required_fields_are_marked_with_asterisk') }} (<span class="red--text">*</span>).
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">

                  <v-row class="section-title" justify="space-between" no-gutters>
                    <div class="text-h6 text-md-h4">
                      {{ $tc('survey_templates.name') }}<span class="red--text">*</span>
                    </div>
                  </v-row>
                  <v-row class="caption mt-2" no-gutters>
                    {{ $t('survey_templates.choose_type_help') }}
                  </v-row>
                  <v-radio-group v-model="surveyTemplate.type">
                    <v-card>
                      <v-card-text>
                        <v-radio :disabled="surveyTemplate.key && surveyTemplate.type === 'MANAGEMENT'"
                                 value="PERSONNEL">
                          <template v-slot:label>
                            <div class="ml-1">
                              <div class="font-weight-bold">{{ $t('surveys.type.PERSONNEL') }}</div>
                              <div class="caption">{{ $t('surveys.personnel_survey_explained') }}</div>
                            </div>
                          </template>
                        </v-radio>
                      </v-card-text>
                      <v-card-text>
                        <v-radio :disabled="surveyTemplate.key && surveyTemplate.type === 'PERSONNEL'"
                                 value="MANAGEMENT">
                          <template v-slot:label>
                            <div class="ml-1">
                              <div class="font-weight-bold">{{ $t('surveys.type.MANAGEMENT') }}</div>
                              <div class="caption">{{ $t('surveys.management_survey_explained') }}</div>
                            </div>
                          </template>
                        </v-radio>
                      </v-card-text>
                      <v-card-text v-if="typeError">
                        <v-alert :value="!!typeError" dense text type="error">
                          {{ typeError }}
                        </v-alert>
                      </v-card-text>
                    </v-card>
                  </v-radio-group>


                </v-col>
                <v-col cols="12">
                  <v-row class="section-title" justify="space-between" no-gutters>
                    <div class="text-h6 text-md-h4">
                      {{ $t('survey_templates.survey_settings') }}
                    </div>
                  </v-row>
                  <v-row class="caption mt-2" no-gutters>
                    {{ $t('surveys.settings_help') }}
                  </v-row>
                  <v-card class="mt-5">

                    <v-card-text>
                      <v-row>
                        <v-col cols="12">
                          <div>
                            <strong>{{ $t('survey_templates.start_text') }}<span class="red--text">*</span></strong>
                            <div class="caption">
                              {{ $t('survey_templates.start_text_explained') }}
                            </div>
                          </div>
                        </v-col>
                        <v-col cols="12">
                          <translation-input :obj.sync="surveyTemplate" :rules="[$rules.required]"
                                             componentType="v-textarea"
                                             field="startTid" prefix="f.question.start_text"/>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-col>

              </v-row>
            </v-col>
          </v-row>
        </v-tab-item>
        <v-tab-item>
          <v-row>
            <v-col cols="12" lg="3" md="4" xl="2">
              <v-navigation-drawer class="w-100" color="transparent" permanent>
                <template v-for="question in surveyTemplate.questions">
                  <question-side-panel :key="question.key || question.tempKey" :question="question"/>
                </template>


              </v-navigation-drawer>

            </v-col>
            <v-col cols="12" lg="9" md="8" xl="10">

              <section-header class="mb-5">
                {{ $tc('questions.title', 2) }}
                <template v-slot:description>
                  {{ $t('questions.title_help') }}
                </template>
              </section-header>
              <template v-for="question in surveyTemplate.questions">

                <edit-question-view :key="question.key || question.tempKey" :question="question"
                                    :questions="surveyTemplate.questions" @add-new-question="addNewQuestion"
                                    @delete-question="deleteQuestion" @duplicate-question="duplicateQuestion"/>
              </template>

              <v-row>
                <v-col cols="12">

                  <v-btn block color="primary" @click="addNewGroup">{{ $t('questions.add_new_question_group') }}</v-btn>

                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-tab-item>
      </v-tabs-items>


    </v-col>


    <template v-if="validationErrors" v-slot:bottom-bar-help>
        <span class="caption">
          <v-icon color="error" size="20" style="margin-top: -2px;">mdi mdi-alert</v-icon>
          {{ $t('surveys.errors.validation_errors') }}
        </span>
    </template>

  </hb-basic-page>
</template>

<script>
import TranslationInput from "@/components/TranslationInput";
import EditQuestionView from "@/components/Question/EditQuestionView";
import SectionHeader from "@/components/SectionHeader";
import QuestionSidePanel from "@/components/Question/QuestionSidePanel";
import surveyTemplateApi from "@/api/surveytemplate";
import TranslationService from "@/service/translation.service";

export default {
  name: 'CreateSurveyTemplateView',
  components: {QuestionSidePanel, SectionHeader, EditQuestionView, TranslationInput},
  data() {
    return {
      loading: true,
      subtleLoading: false,
      tab: null,
      surveyTemplate: {
        type: '',
        startTid: '',
        start: '',
        welcome: true,
        autojump: true,
        questions: [],
      },
      typeError: false,
      datesError: false,
      validationErrors: false,

      actionButtons: {
        cancel: {
          text: this.$t('cancel'),
          icon: 'arrow_back',
          callback: this.cancel,
          primary: false,
        },
        save: {
          text: this.$t('save'),
          icon: 'mdi mdi-check',
          callback: this.saveSurvey,
          primary: true,
          loading: false,
        },
      }
    };
  },
  methods: {
    async saveSurvey() {
      // this.actionButtons.save.loading = true;
      // this.actionButtons.cancel.disabled = true;

      this.typeError = false;
      this.datesError = false;
      this.validationErrors = false;
      if (!this.surveyTemplate.type) {
        this.typeError = this.$t('surveys.errors.type_error');
        this.validationErrors = true;
      }
      if (this.validationErrors) {
        this.actionButtons.save.loading = false;
        this.actionButtons.cancel.disabled = false;
        return;
      }

      // @todo tallennus ja siirtymä


      try {
        await TranslationService.saveNewTranslations();
      } catch (e) {
        console.error(e)
      }
      const surveyTemplate = {...this.surveyTemplate};
      let curOrder = 10;
      surveyTemplate.questions?.map(q => {
        q.order = curOrder;
        curOrder += 10;

        let gCurOrder = 10;
        if (q.choiceList) {
          q.choiceList.map(c => {
            c.order = gCurOrder;
            gCurOrder += 10;
          })
        }
      });

      try {
        const s = await surveyTemplateApi.createSurveyTemplate(surveyTemplate);
        this.$showSuccessNotification(this.$t('survey_templates.messages.saved'));
        this.$router.replace({name: 'edit_survey_template', params: {key: s.key}});
      } catch (e) {
        this.$handleApiError(e, this.$t('survey_templates.errors.error_saving'))
      }

      this.actionButtons.save.loading = false;
      this.actionButtons.cancel.disabled = false;
    },
    cancel() {
      this.$router.go(-1);
    },
    addNewGroup() {
      this.surveyTemplate.questions.push({
        key: null,
        titleTid: null,
        contentTid: null,
        type: 'GROUP',
        order: null,
        group: null,
        sliderMin: null,
        sliderMax: null,
        sliderStep: null,
        sliderMinTid: null,
        sliderMaxTid: null,
        choiceLimit: null,
        value: null,
        choiceList: [],
        tempKey: 'TMP_' + this.$randomString(),
      });
    },
    addNewQuestion(groupQuestion) {
      const groupIndex = this.surveyTemplate.questions.findIndex(q => (groupQuestion.tempKey && q.tempKey === groupQuestion.tempKey) || (groupQuestion.key && q.key === groupQuestion.key));
      this.surveyTemplate.questions.splice(groupIndex > -1 ? groupIndex + 1 : this.surveyTemplate.questions.length, 0, {
        key: null,
        titleTid: null,
        contentTid: null,
        type: null,
        order: null,
        group: groupQuestion.group || groupQuestion.key || groupQuestion.tempKey,
        sliderMin: null,
        sliderMax: null,
        sliderStep: null,
        sliderMinTid: null,
        sliderMaxTid: null,
        choiceLimit: null,
        value: null,
        choiceList: [],
        tempKey: 'TMP_' + this.$randomString(),
      });
    },
    deleteQuestion(question) {
      const questionIndex = this.surveyTemplate.questions.findIndex(q => (question.tempKey && q.tempKey === question.tempKey) || (question.key && q.key === question.key));
      if (questionIndex > -1) {
        this.surveyTemplate.questions.splice(questionIndex, 1);
      }
    },
    duplicateQuestion(question) {
      const questionIndex = this.surveyTemplate.questions.findIndex(q => (question.tempKey && q.tempKey === question.tempKey) || (question.key && q.key === question.key));
      if (questionIndex > -1) {
        const duplicatedQuestion = {
          ...question,
          tempKey: 'TMP_' + this.$randomString(),
          key: null,
          choiceList: [...question.choiceList.map(c => ({...c, key: null, tempKey: 'TMP_' + this.$randomString()}))]
        };
        this.surveyTemplate.questions.splice(questionIndex + 1, 0, duplicatedQuestion);
      }
    },
    async getSurveyTemplate() {
      this.loading = true;
      try {
        this.surveyTemplate = await surveyTemplateApi.getSurveyTemplate(this.$route.params.key);

      } catch (e) {
        this.$handleApiError(e, this.$t('errors.error_loading_surveytemplate'))
      }
      this.loading = false;
    }


  },
  async mounted() {
    if (!this.$hasMatchingRoles(['admin'])) {
      this.$router.replace({name: 'access_denied'});
      return;
    }
    this.loading = false;
    if (this.$route.params.key) {
      await this.getSurveyTemplate();
    }
  },
};
</script>

<style lang="scss" scoped>
.section-title {
  border-bottom: 2px solid var(--v-primary-base) !important;
}

.empty-spacer {
  height: 800px;
}
</style>

<style lang="scss">

.create-survey-template-view {
  .v-tabs > .v-tabs-bar, .v-tabs-items {
    background-color: transparent !important;
  }

  .w-100 {
    width: 100% !important;
  }
}
</style>
